import React from 'react';
import { PublicTemplate } from '../../types/publicTypes';
import { useNavigation } from '../../context/NavigationContext';
import { SafeImage } from './SafeImage';
import {
  Eye,
  ArrowRight,
  Star,
  CheckCircle2,
  Sparkles,
  Smartphone,
  Monitor,
} from 'lucide-react';

interface TemplateCardProps {
  template: PublicTemplate;
  onPreview: (template: PublicTemplate) => void;
}

export const TemplateCard: React.FC<TemplateCardProps> = ({ template, onPreview }) => {
  const { navigate } = useNavigation();

  return (
    <div className="group bg-slate-900/70 border border-slate-800/90 rounded-2xl overflow-hidden hover:border-slate-700 transition-all duration-300 flex flex-col shadow-lg shadow-black/20 hover:shadow-2xl hover:shadow-blue-600/10">
      {/* Thumbnail Container with Action Overlay */}
      <div className="relative aspect-[16/10] overflow-hidden bg-slate-950">
        <SafeImage
          src={template.thumbnail}
          alt={template.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />

        {/* Gradient Overlay on Hover */}
        <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-3 p-4">
          <button
            onClick={() => onPreview(template)}
            className="px-4 py-2.5 rounded-xl bg-slate-900/90 hover:bg-slate-900 text-white text-xs font-semibold border border-slate-700 shadow-md flex items-center gap-1.5 transition-transform active:scale-95"
          >
            <Eye className="w-3.5 h-3.5 text-blue-400" />
            <span>Canlı Önizle</span>
          </button>
          <button
            onClick={() => navigate('/kayit')}
            className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold shadow-lg shadow-blue-600/30 flex items-center gap-1.5 transition-transform active:scale-95"
          >
            <span>Şablonu Seç</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Top Badges */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-slate-950/80 backdrop-blur-md text-white border border-slate-800">
            {template.categoryName}
          </span>
          {template.popular && (
            <span className="px-2 py-1 rounded-md text-[10px] font-bold bg-amber-500/20 backdrop-blur-md text-amber-300 border border-amber-500/30 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              Popüler
            </span>
          )}
        </div>

        {/* Rating badge */}
        <div className="absolute bottom-3 right-3 px-2 py-1 rounded-md text-[11px] font-medium bg-slate-950/85 backdrop-blur-md text-slate-200 border border-slate-800/80 flex items-center gap-1">
          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
          <span>{template.rating}</span>
          <span className="text-slate-500">({template.reviewsCount})</span>
        </div>
      </div>

      {/* Content Body */}
      <div className="p-5 flex-1 flex flex-col justify-between">
        <div>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors">
              {template.name}
            </h3>
            <div className="flex items-center gap-1 text-slate-500 text-xs">
              <Monitor className="w-3.5 h-3.5" />
              <Smartphone className="w-3.5 h-3.5" />
            </div>
          </div>

          <p className="text-slate-400 text-xs line-clamp-2 leading-relaxed mb-4">
            {template.description}
          </p>

          {/* Feature Badges */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {template.features.slice(0, 3).map((feat, i) => (
              <span
                key={i}
                className="px-2 py-0.5 rounded-md text-[11px] bg-slate-800/60 text-slate-300 border border-slate-700/50 flex items-center gap-1"
              >
                <CheckCircle2 className="w-2.5 h-2.5 text-blue-400" />
                {feat}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-4 border-t border-slate-800/80 flex items-center justify-between text-xs">
          <span className="text-slate-500">
            {template.pages.length} Hazır Sayfa
          </span>
          <button
            onClick={() => onPreview(template)}
            className="text-blue-400 hover:text-blue-300 font-semibold flex items-center gap-1 group/btn"
          >
            <span>İncele</span>
            <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};
